import { useEffect, useMemo, useSyncExternalStore } from "react";
import { getStoreSettingsFn, saveStoreSettingsFn } from "@/lib/settings/settings.functions";
import { shippingConfigFromSettings, type ShippingConfig } from "@/lib/cart/pricing";
import type { WhatsAppChatSettings } from "@/lib/whatsapp-chat/config";
import type { HomeAnnouncement, HomePromo } from "@/lib/home-promo/config";

const EVT = "cb16:admin-settings";

export type StoreAddress = {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type StoreContact = {
  phone: string;
  whatsapp: string;
  email: string;
};

/** Business hours shown on the Contact page. */
export type StoreHours = {
  weekdays: string;
  saturday: string;
  sunday: string;
};

export type StoreTax = {
  enabled: boolean;
  ratePct: number;      // legacy combined rate — kept for backward compatibility
  /** Intra-state CGST rate (%). */
  cgstRatePct?: number;
  /** Intra-state SGST rate (%). */
  sgstRatePct?: number;
  /** Inter-state IGST rate (%). */
  igstRatePct?: number;
  inclusive: boolean;   // whether displayed prices already include tax
  label: string;        // e.g. "GST"
  /** Seller GSTIN (printed on invoices). */
  gstin?: string;
  /** Business state — place of supply origin (Maharashtra for CB16). */
  businessState?: string;
  /** ISO date; GST is only applied to orders on/after this date. */
  effectiveFrom?: string;
  /** Default HSN / tax classification shown on invoices. */
  defaultHsn?: string;
};


export type StoreShipping = {
  freeThreshold: number;      // in minor major units (rupees/euros)
  standardFee: number;
  expressFee: number;
  standardEtaDays: string;    // "3–5"
  expressEtaDays: string;     // "1–2"
};

export type StoreSeo = {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  ogImage: string;
};

export type StoreFooter = {
  tagline: string;
  copyright: string;
};

export type StoreBranding = {
  logoUrl: string;
  wordmarkUrl: string;
  favicon16: string;
  favicon32: string;
  favicon180: string;
  themeColor: string;
  accentColor: string;
};

export type StorePayments = {
  methods: Array<"cod" | "upi" | "card" | "netbanking" | "wallet">;
  instructions: string;
  testMode: boolean;
};

export type StoreReturns = {
  windowDays: number;
  policySummary: string;
  requiresReason: boolean;
  restockingFeePct: number;
};

export type StoreSocial = {
  instagram: string;
  facebook: string;
  x: string;
  youtube: string;
  linkedin: string;
  pinterest: string;
  tiktok: string;
};

export type StoreLegal = {
  gstin: string;
  cin: string;
  pan: string;
  termsUrl: string;
  privacyUrl: string;
  refundUrl: string;
  shippingUrl: string;
};

export type StoreNotifications = {
  orderPlacedEmail: boolean;
  lowStockEmail: boolean;
  dailyDigestEmail: boolean;
};

export type StoreStatus = {
  mode: "open" | "paused" | "maintenance";
  message: string;
};

export type AdminSettings = {
  // Business / brand
  storeName: string;
  legalName: string;
  brandTagline: string;
  supportEmail: string;
  logoUrl: string;
  branding: StoreBranding;
  // Localisation
  currency: string;
  locale: string;
  timezone: string;
  // Contact & address
  address: StoreAddress;
  contact: StoreContact;
  hours: StoreHours;
  // Commerce
  tax: StoreTax;
  shipping: StoreShipping;
  payments: StorePayments;
  returns: StoreReturns;
  // Marketing / content
  seo: StoreSeo;
  footer: StoreFooter;
  social: StoreSocial;
  legal: StoreLegal;
  notifications: StoreNotifications;
  storeStatus: StoreStatus;
  /** Website WhatsApp click-to-chat widget (read via resolveWhatsAppChatSettings). */
  whatsappChat?: Partial<WhatsAppChatSettings>;
  /** Homepage go-live announcement (read via resolveHomeAnnouncement). */
  homeAnnouncement?: Partial<HomeAnnouncement>;
  /** Homepage promotional banner (read via resolveHomePromo). */
  homePromo?: Partial<HomePromo>;
  // System
  maintenance: boolean;
};

export const DEFAULT_SETTINGS: AdminSettings = {
  storeName: "CB16 Atelier",
  legalName: "CB16 Atelier Pvt. Ltd.",
  brandTagline: "Luxury fragrances, crafted in India.",
  supportEmail: "supportcb16.in@gmail.com",
  logoUrl: "",
  branding: {
    logoUrl: "",
    wordmarkUrl: "",
    favicon16: "",
    favicon32: "",
    favicon180: "",
    themeColor: "#0F0F10",
    accentColor: "#C6A15B",
  },
  currency: "INR",
  locale: "en-IN",
  timezone: "Asia/Kolkata",
  address: {
    line1: "12 Marine Drive",
    line2: "Nariman Point",
    city: "Mumbai",
    state: "Maharashtra",
    postalCode: "400021",
    country: "India",
  },
  contact: {
    phone: "+91 22 0000 0000",
    whatsapp: "+91 90000 00000",
    email: "supportcb16.in@gmail.com",
  },
  hours: {
    weekdays: "10:00 – 19:00 IST",
    saturday: "11:00 – 18:00 IST",
    sunday: "By appointment only",
  },
  tax: {
    enabled: true,
    ratePct: 18,
    cgstRatePct: 9,
    sgstRatePct: 9,
    igstRatePct: 18,
    inclusive: true,
    label: "GST",
    gstin: "",
    businessState: "Maharashtra",
    effectiveFrom: "",
    defaultHsn: "",
  },

  shipping: {
    freeThreshold: 2500,
    standardFee: 150,
    expressFee: 350,
    standardEtaDays: "3–5",
    expressEtaDays: "1–2",
  },
  payments: {
    methods: ["cod", "upi", "card"],
    instructions: "COD available on prepaid-verified addresses.",
    testMode: true,
  },
  returns: {
    windowDays: 14,
    policySummary: "Unopened items may be returned within 14 days of delivery.",
    requiresReason: true,
    restockingFeePct: 0,
  },
  seo: {
    metaTitle: "CB16 Atelier — Luxury Fragrance House",
    metaDescription:
      "Artisanal perfumes, diffusers, and home fragrances crafted with rare essences.",
    keywords: "luxury perfume, artisan fragrance, oud, attar, diffuser",
    ogImage: "",
  },
  footer: {
    tagline: "Composed in India. Worn worldwide.",
    copyright: "© CB16 Atelier. All rights reserved.",
  },
  social: {
    instagram: "https://instagram.com/cb16atelier",
    facebook: "",
    x: "",
    youtube: "",
    linkedin: "",
    pinterest: "",
    tiktok: "",
  },
  legal: {
    gstin: "",
    cin: "",
    pan: "",
    termsUrl: "/terms",
    privacyUrl: "/privacy",
    refundUrl: "/returns",
    shippingUrl: "/shipping",
  },
  notifications: {
    orderPlacedEmail: true,
    lowStockEmail: true,
    dailyDigestEmail: false,
  },
  storeStatus: {
    mode: "open",
    message: "",
  },
  maintenance: false,
};

export const SUPPORTED_CURRENCIES: Array<{ code: string; label: string; locale: string }> = [
  { code: "INR", label: "Indian Rupee (₹)", locale: "en-IN" },
  { code: "USD", label: "US Dollar ($)", locale: "en-US" },
  { code: "EUR", label: "Euro (€)", locale: "en-GB" },
  { code: "GBP", label: "British Pound (£)", locale: "en-GB" },
  { code: "AED", label: "UAE Dirham (د.إ)", locale: "en-AE" },
];

/**
 * Deep-merge a stored settings record onto the defaults so newly-introduced
 * fields always have a value.
 */
function merge(parsed: Partial<AdminSettings>): AdminSettings {
  return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      address: { ...DEFAULT_SETTINGS.address, ...(parsed.address ?? {}) },
      contact: { ...DEFAULT_SETTINGS.contact, ...(parsed.contact ?? {}) },
      hours: { ...DEFAULT_SETTINGS.hours, ...(parsed.hours ?? {}) },
      tax: { ...DEFAULT_SETTINGS.tax, ...(parsed.tax ?? {}) },
      shipping: { ...DEFAULT_SETTINGS.shipping, ...(parsed.shipping ?? {}) },
      seo: { ...DEFAULT_SETTINGS.seo, ...(parsed.seo ?? {}) },
      footer: { ...DEFAULT_SETTINGS.footer, ...(parsed.footer ?? {}) },
      branding: { ...DEFAULT_SETTINGS.branding, ...(parsed.branding ?? {}) },
      payments: { ...DEFAULT_SETTINGS.payments, ...(parsed.payments ?? {}) },
      returns: { ...DEFAULT_SETTINGS.returns, ...(parsed.returns ?? {}) },
      social: { ...DEFAULT_SETTINGS.social, ...(parsed.social ?? {}) },
      legal: { ...DEFAULT_SETTINGS.legal, ...(parsed.legal ?? {}) },
      notifications: { ...DEFAULT_SETTINGS.notifications, ...(parsed.notifications ?? {}) },
      storeStatus: { ...DEFAULT_SETTINGS.storeStatus, ...(parsed.storeStatus ?? {}) },
  };
}

/* ---------------------------------------------------------------------------
 * Store settings live in the database (`store_settings`) — that row is the
 * single source of truth for every surface (Contact page, Footer, Admin).
 * This module keeps one in-memory snapshot, loads it once per session and
 * notifies subscribers whenever it changes, so a save in Admin updates the
 * storefront immediately without a refresh.
 * ------------------------------------------------------------------------ */

let current: AdminSettings = DEFAULT_SETTINGS;
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVT));
}

export async function loadStoreSettings(force = false): Promise<void> {
  if (inflight) return inflight;
  if (loaded && !force) return;
  inflight = (async () => {
    try {
      const raw = await getStoreSettingsFn();
      current = merge((raw ?? {}) as Partial<AdminSettings>);
      loaded = true;
      emit();
    } catch {
      /* keep defaults; a transient read failure must not blank the UI */
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Persist settings to the database and update every subscriber. */
export async function saveSettings(next: AdminSettings): Promise<void> {
  current = next;
  loaded = true;
  emit();
  await saveStoreSettingsFn({
    data: { settings: JSON.parse(JSON.stringify(next)) as Record<string, never> },
  });
  await loadStoreSettings(true);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function useAdminSettings(): [AdminSettings, (n: AdminSettings) => Promise<void>] {
  const state = useSyncExternalStore(subscribe, () => current, () => DEFAULT_SETTINGS);
  useEffect(() => { void loadStoreSettings(); }, []);
  return [state, saveSettings];
}

/**
 * Read-only alias for consumers that don't need the setter.
 * Use `useStoreSettings()` in feature code; use `useAdminSettings()` on the
 * Settings page where a save handler is needed.
 */
/** True once the tenant's saved settings have been read in this browser session. */
export function useStoreSettingsLoaded(): boolean {
  const state = useSyncExternalStore(subscribe, () => loaded, () => false);
  useEffect(() => { void loadStoreSettings(); }, []);
  return state;
}

export function useStoreSettings(): AdminSettings {
  const [state] = useAdminSettings();
  return state;
}

/** Customer-facing delivery charges from Settings → Shipping (same source the server validates). */
export function useShippingConfig(): ShippingConfig {
  const settings = useStoreSettings();
  return useMemo(
    () => shippingConfigFromSettings(settings) ?? shippingConfigFromSettings(DEFAULT_SETTINGS)!,
    [settings],
  );
}

/**
 * Format an amount using the configured Store Settings currency + locale.
 * Used across the Admin Portal so no monetary value is hardcoded.
 */
export function formatAdminMoney(
  amount: number,
  currency: string,
  locale: string,
): string {
  try {
    return new Intl.NumberFormat(locale || "en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount)}`;
  }
}

export type AdminMoney = {
  (amount: number): string;
  currency: string;
  symbol: string;
};

function currencySymbol(currency: string, locale: string) {
  try {
    const parts = new Intl.NumberFormat(locale || "en-IN", {
      style: "currency",
      currency: currency || "INR",
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

export function useAdminMoney(): AdminMoney {
  const [settings] = useAdminSettings();
  return useMemo(() => {
    const fmt = ((n: number) =>
      formatAdminMoney(n, settings.currency, settings.locale)) as AdminMoney;
    fmt.currency = settings.currency;
    fmt.symbol = currencySymbol(settings.currency, settings.locale);
    return fmt;
  }, [settings.currency, settings.locale]);
}
